import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import API from '../utils/api';
import './VerifyCertificate.css';

/* ─── SVG ICONS ───────────────────────────────────────────── */
const SearchIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const XCircleIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const BookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const TagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 1 1 8 0v3" />
  </svg>
);

const VerifyCertificate = () => {
  const { certId } = useParams();
  const [input, setInput] = useState(certId || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    setResult(null);
    setSearched(false);
    try {
      const { data } = await API.get(`/certificates/verify/${input.trim()}`);
      setResult(data);
    } catch {
      setResult({ valid: false });
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  return (
    <main className="certificate-page">
      <section className="certificate-hero">
        <div className="container certificate-hero-inner">
          <div className="certificate-hero-copy">
            <div className="certificate-eyebrow"><span /> Trusted credentials, verified</div>
            <h1>Proof that<br /><span>speaks for you.</span></h1>
            <p className="certificate-hero-description">
              Verify a ThiranX certificate in seconds. Confirm its authenticity and view the achievement details with confidence.
            </p>
            <div className="certificate-hero-points">
              <span><b aria-hidden="true">✓</b> Instant verification</span>
              <span><b aria-hidden="true">✓</b> Official ThiranX records</span>
            </div>
          </div>

          <div className="certificate-visual" aria-label="ThiranX certificate verification illustration">
            <div className="certificate-visual-top">
              <span className="certificate-visual-brand">THIRANX</span>
              <span className="certificate-visual-number">CREDENTIAL / 01</span>
            </div>
            <div className="certificate-seal"><ShieldIcon /></div>
            <div className="certificate-visual-caption">
              <strong>Certificate of Achievement</strong>
              <span>AUTHENTIC · VERIFIED · YOURS</span>
            </div>
            <div className="certificate-visual-bottom">
              <span>ThiranX Learning &amp; Certification</span>
              <span>VERIFIED</span>
            </div>
          </div>
        </div>
      </section>

      <section className="certificate-main">
        <div className="container">
          <div className="certificate-layout">
            <div>
              <div className="certificate-panel certificate-lookup">
                <div className="certificate-panel-eyebrow">Certificate lookup</div>
                <h2>Verify a certificate</h2>
                <p>Enter the unique ID printed on the certificate to check its status.</p>

                <form className="certificate-form" onSubmit={handleVerify}>
                  <label htmlFor="certificate-id">Certificate ID</label>
                  <div className="certificate-input-wrap">
                    <TagIcon />
                    <input
                      id="certificate-id"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      placeholder="e.g. TXCERT-1714123456-ABC123"
                      required
                      autoComplete="off"
                    />
                  </div>
                  <button className="certificate-submit" type="submit" disabled={loading || !input.trim()}>
                    {loading ? <><span className="certificate-spinner" aria-hidden="true" /> Verifying certificate…</> : <><SearchIcon /> Verify certificate</>}
                  </button>
                </form>

                <div className="certificate-secure-note"><LockIcon /> Your search is checked against official ThiranX records</div>
              </div>

              {searched && result && (
                result.valid ? (
                  <div className="certificate-panel certificate-result" aria-live="polite">
                    <div className="certificate-result-heading">
                      <CheckCircleIcon />
                      <div>
                        <span>Verification successful</span>
                        <h2>Certificate is authentic</h2>
                        <p>This certificate was issued by ThiranX.</p>
                      </div>
                    </div>
                    <div className="certificate-details">
                      {[
                        { icon: <UserIcon />, label: 'Student name', value: result.studentName },
                        { icon: <BookIcon />, label: 'Course', value: result.courseName },
                        { icon: <CalendarIcon />, label: 'Issued date', value: new Date(result.issuedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) },
                        { icon: <TagIcon />, label: 'Certificate ID', value: result.certificateId, className: 'certificate-detail-id' },
                      ].map(({ icon, label, value, className = '' }) => (
                        <div className={`certificate-detail ${className}`} key={label}>
                          <span className="certificate-detail-label">{icon}{label}</span>
                          <strong className="certificate-detail-value">{value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="certificate-panel certificate-result certificate-invalid" aria-live="polite">
                    <XCircleIcon />
                    <h2>Certificate not found</h2>
                    <p>We couldn’t find this ID in our records. Check the characters and try again, or contact us if you need help.</p>
                  </div>
                )
              )}
            </div>

            <aside className="certificate-panel certificate-help">
              <div className="certificate-panel-eyebrow">Quick guide</div>
              <h2>How verification works</h2>
              <p>A simple way to confirm a learner’s achievement.</p>
              <div className="certificate-steps">
                <div className="certificate-step">
                  <span className="certificate-step-number">01</span>
                  <div><strong>Find the certificate ID</strong><span>Look for the unique ID on the certificate PDF.</span></div>
                </div>
                <div className="certificate-step">
                  <span className="certificate-step-number">02</span>
                  <div><strong>Enter it above</strong><span>Type or paste the ID into the lookup field.</span></div>
                </div>
                <div className="certificate-step">
                  <span className="certificate-step-number">03</span>
                  <div><strong>Confirm the details</strong><span>We’ll show the verified learner and course information.</span></div>
                </div>
              </div>
            </aside>
          </div>

          <footer className="certificate-footer">
            <span>Need help locating your certificate ID?</span>
            <nav aria-label="Certificate verification links">
              <Link to="/dashboard">My Dashboard</Link>
              <Link to="/courses">Browse courses</Link>
              <Link to="/">Back to home</Link>
            </nav>
          </footer>
        </div>
      </section>
    </main>
  );
};

export default VerifyCertificate;
